import {
  Component, EventEmitter, HostBinding, OnDestroy, OnInit,
  Output,
} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {Router} from '@angular/router';
import {GuiZoneService, ProjectService} from '@catcore/services';
import {EntitySchemaService} from '@catcore/services/entity-schema.service';
import {GuiZone, Project} from '@catcore/types/api-v2';
import {GenericLinkedResource} from '@catcore/types/base';
import {EntitySchemaType} from '@catcore/utils/json-schema/entity-schema-to-json-schema';
import {GuiZoneItem, GuiZoneList} from '@catmodels/gui-zone/types';
import {LinkItem, StyleType} from '@catui/types';
import {EntityForm} from '@catuimodels/shared/entity-form';
import {SelectItem} from 'primeng/primeng';
import {Subscription} from 'rxjs/Subscription';
import {isNullOrUndefined} from 'util';
import {ModalService} from "@catmodules/widgets/slide-in-modal/services/modal.service";
import {DeleteGuiZoneComponent} from "@catmodels/gui-zone/components/delete-gui-zone/delete-gui-zone.component";
import {GenericResourceService} from "@catfeatures/services/generic-resource.service";
import {AddGuiZoneComponent} from "@catmodels/gui-zone/components/add-gui-zone/add-gui-zone.component";

const componentBaseName = 'gui-zone-form';
const selectorName = 'cat-' + componentBaseName;
const defaultClassNames = [selectorName].join(' ');

/**
 * This component provides the required methods to display and manage the form for editing a GuiZone model
 */
@Component({
  selector: 'cat-gui-zone-form',
  styleUrls: ['./gui-zone-form.component.scss'],
  templateUrl: './gui-zone-form.component.html',
})
export class GuiZoneFormComponent extends EntityForm implements OnInit, OnDestroy {
  @HostBinding('class') classNames = defaultClassNames;
  labels = {
    name: 'Name',
    shortName: 'Short name',
    description: 'Description',
    sortIndex: 'Sort Index',
    zonePa: 'PA Zone',
    type: 'Type',
    groupType: 'Group Type',
    parentGuiZone: 'Parent GUI Zone',
    imageX: 'Image X',
    imageY: 'Image Y',
    iconName: 'Icon name',
    icon: 'Icon',
    lopaName: 'Lopa name',
    lopa: 'Lopa',
    lopaPos: 'Lopa Position',
    lightZone: 'Light Zone',
  };

  ZONE_GROUP = 'zoneGroup';

  typeOptions: SelectItem[];
  showGroupType: boolean = false;
  showDescription: boolean = true;
  groupTypeOptions: SelectItem[];
  guiZoneOptions: SelectItem[];
  parents: LinkItem[] = [];
  children: LinkItem[] = [];
  styleType = StyleType;
  private modelSchema;
  private project: Project;
  private subscription: Subscription;
  private afterDelSubscription: Subscription = null;
  private afterAddSubscription: Subscription = null;
  private guiZone = null;

  private projectSubscription: Subscription;
  private parentsSubscription: Subscription;
  private childrenSubscription: Subscription;

  constructor(
    formBuilder: FormBuilder,
    schemaService: EntitySchemaService,
    private projectService: ProjectService,
    private guiZoneService: GuiZoneService,
    private modalService: ModalService,
    private genericService: GenericResourceService,
  ) {
    super(formBuilder);
    this.setSubmitNullValueFields(['group_type', 'lopa', 'lopa_name', 'icon', 'icon_name']);
    this.modelSchema = schemaService.getForEntityName(EntitySchemaType['GuiZones']);
    this.setUpdateIgnore(['parents', 'children']);
  }

  ngOnInit() {
    this.projectSubscription = this.projectService
      .getCurrent()
      .subscribe((project: Project) => {
        if (!isNullOrUndefined(project)) {
          this.project = project;
          const guiZones = this.projectService.extractGuiZonesFrom(this.project);
          this.updateGuiZoneOptions(guiZones);
        }
      });

    if (this.modelSchema) {
      this.updateChoicesOptions(this.modelSchema.properties.type.choices);
      this.updateGroupTypeOptions(this.modelSchema.properties.group_type.choices);
    }
  }

  ngOnDestroy() {
    if (this.parentsSubscription) {
      this.parentsSubscription.unsubscribe();
    }
    if (this.childrenSubscription) {
      this.childrenSubscription.unsubscribe();
    }
    if (this.projectSubscription) {
      this.projectSubscription.unsubscribe();
    }
  }

  updateShowGroupType($event) {
    console.log('Event: ', $event);
    this.showGroupType = ($event.value === this.ZONE_GROUP);
    if (!this.showGroupType && this.form) {
      this.form.patchValue({group_type: null});
    }
    this.showDescription = ($event.value !== this.ZONE_GROUP);
  }

  /**
   * read the provided file-stream and set it as the to be used icon file
   * @param $event
   */
  updateIconFile($event) {
    this.updateFile($event, (name: string, value: string) => {
      this.form.patchValue({icon_name: name});
      this.form.patchValue({icon: value});
      this.form.get('icon_name').markAsDirty();
      this.form.get('icon').markAsDirty();
    });
  }

  clearIconFile() {
    this.form.patchValue({
      icon_name: null,
      icon: null,
    });
    this.form.get('icon_name').markAsDirty();
    this.form.get('icon').markAsDirty();
  }

  /**
   * read the provided file-stream and set it as the to be used lopa file
   * @param $event
   */
  updateLopaFile($event) {
    this.updateFile($event, (name: string, value: string) => {
      this.form.patchValue({lopa_name: name});
      this.form.patchValue({lopa: value});
      this.form.get('lopa_name').markAsDirty();
      this.form.get('lopa').markAsDirty();
    });
  }

  clearLopaFile() {
    this.form.patchValue({
      lopa_name: null,
      lopa: null,
    });
    this.form.get('lopa_name').markAsDirty();
    this.form.get('lopa').markAsDirty();
  }

  private updateFile($event, onFinish: (name: string, value: string) => void) {
    const fileReader = new FileReader();
    const fileInfo = $event.target.files[0];
    const fileName = fileInfo.name;
    fileReader.onload = (fileEvent: any) => {
      const uploadTarget: any = fileEvent.target;
      const fileContent = `${encodeURI(uploadTarget.result)}`;
      onFinish(fileName, fileContent);
    };
    fileReader.readAsDataURL(fileInfo);
  }

  /**
   * Refresh currenty view after form was submitted
   * @param result
   */
  protected afterFormSubmit(result: any) {
    this.projectService.refreshCurrent();
  }

  /**
   * Refresh current view after GUI zone was deleted
   */
  protected afterDelete() {
    this.projectService.refreshCurrent();
    this.form = null;
  }

  /**
   * Request parents and children of GUI zone once the entity was received from the backend
   *
   * @param value GUI zone entity as defined in schema
   */
  protected onEntityReceived(value: any) {
    this.guiZone = value;
    this.buildParentList(value);
    this.buildChildrenList(value);
    this.updateShowGroupType({value: value.type});
  }

  addChild() {
    const guiZoneResource = {label: this.guiZone.name, href: this.guiZone._links.self.href};
    this.openDialog(guiZoneResource, (guiZone) => this.addChildModalFactory(guiZone));
  }

  /**
   * Update the available GUI zone types
   *
   * @param {string[]} typeChoices
   */
  private updateChoicesOptions(typeChoices: string[]) {
    const items: SelectItem[] = [];
    typeChoices.forEach((name: string) => items.push({label: name, value: name}));
    this.typeOptions = items;
  }

  /**
   * Update the available GUI zone types
   *
   * @param {string[]} groupTypeChoices
   */
  private updateGroupTypeOptions(groupTypeChoices: string[]) {
    const items: SelectItem[] = [];
    groupTypeChoices.forEach((name: string) => items.push({label: name, value: name}));
    this.groupTypeOptions = items;
  }

  /**
   * Update the available GUI zones to select as a child
   *
   * @param {GuiZone[]} guiZones
   */
  private updateGuiZoneOptions(guiZones: GuiZone[]) {
    const items: SelectItem[] = [];
    items.push({label: '', value: null});
    guiZones.forEach((guiZone: GuiZone) => items.push({label: guiZone.name, value: guiZone._links.self.href}));
    this.guiZoneOptions = items;
  }

  /**
   * Build the list of parents received as link items to open slide in modals on selection
   *
   * @param guiZone
   */
  private buildParentList(guiZone) {
    const items: LinkItem[] = [];
    this.guiZoneService.getParentsFor(guiZone)
      .subscribe((parents: GuiZoneList) => {
        if (!isNullOrUndefined(parents) && ('_embedded' in parents)) {
          parents._embedded.item.forEach(
            (parent: GuiZoneItem) => {
              // create list of link items here including routing function for parents
              const value = {label: parent.name, href: parent._links.self.href};
              items.push({label: value.label, raw: value.href, navigate: (router: Router) => {this.openDeleteParentDialog(value); }});
            });
        }
      });
    this.parents = items;
  }

  /**
   * Build the list of children received as link items to open slide in modals on selection
   *
   * @param guiZone
   */
  private buildChildrenList(guiZone) {
    const items: LinkItem[] = [];
    this.guiZoneService.getChildrenFor(guiZone)
      .subscribe((children: GuiZoneList) => {
        if (!isNullOrUndefined(children) && ('_embedded' in children)) {
          children._embedded.item.forEach(
            (child: GuiZoneItem) => {
              // create list of link items here including routing function for children
              const value = {label: child.name, href: child._links.self.href};
              items.push({label: value.label, raw: value.href, navigate: (router: Router) => {this.openDeleteChildDialog(value); }});
            });
        }
      });
    this.children = items;
  }

  /**
   * Calls the open dialog function with necessary parental modal factory
   *
   * @param {GenericLinkedResource} guiZoneResource
   */
  private openDeleteParentDialog(guiZoneResource: GenericLinkedResource) {
    this.openDialog(guiZoneResource, (guiZone) => this.parentModalFactory(guiZone));
  }

  /**
   * Creates the slide in modal component parameterized for showing and deleting parent GUI zones.
   *
   * @param guiZone
   * @returns {ModalRef}
   */
  private parentModalFactory(guiZone) {
    const compRef = this.modalService.open(DeleteGuiZoneComponent);
    compRef.componentInstance.guiZoneEntity = guiZone;
    compRef.componentInstance.parent = true;
    compRef.componentInstance.affectedGuiZone = this.guiZone;
    compRef.componentInstance.affectedList = this.parents;
    this.afterDelSubscription = compRef.componentInstance.afterDelete.subscribe(() => this.updateLists());
    return compRef;
  }

  /**
   * Calls the open dialog function with necessary child modal factory
   *
   * @param {GenericLinkedResource} guiZoneResource
   */
  private openDeleteChildDialog(guiZoneResource: GenericLinkedResource) {
    this.openDialog(guiZoneResource, (guiZone) => this.childModalFactory(guiZone));
  }

  /**
   * Creates the slide in modal component parameterized for showing and deleting child GUI zones.
   *
   * @param guiZone
   * @returns {ModalRef}
   */
  private childModalFactory(guiZone) {
    const compRef = this.modalService.open(DeleteGuiZoneComponent);
    compRef.componentInstance.guiZoneEntity = guiZone;
    compRef.componentInstance.parent = false;
    compRef.componentInstance.affectedGuiZone = this.guiZone;
    compRef.componentInstance.affectedList = this.children;
    this.afterDelSubscription = compRef.componentInstance.afterDelete.subscribe(() => this.updateLists());
    return compRef;
  }

  private addChildModalFactory(guiZone) {
    const compRef = this.modalService.open(AddGuiZoneComponent);
    compRef.componentInstance.affectedZone = guiZone;
    compRef.componentInstance.children = this.children;
    compRef.componentInstance.parents = this.parents;
    compRef.componentInstance.availableZones = this.guiZoneOptions;
    this.afterAddSubscription = compRef.componentInstance.afterAdd.subscribe(() => this.updateLists());
    return compRef;
  }

  /**
   * Opens the modal with callbacks to fullfill and error using specifed component factory.
   *
   * @param {GenericLinkedResource} resource
   * @param componentFactory
   */
  private openDialog(resource: GenericLinkedResource, componentFactory) {
    let compRef;
    const onFullfill = () => {
      this.subscription.unsubscribe();
    };

    const onError = () => {
      this.subscription.unsubscribe();
    };

    const resourceHandler = this
      .genericService
      .findServiceForResource(resource.href);

    this.subscription = resourceHandler
      .getCurrent()
      .subscribe((item) => {
        if (compRef) {
          compRef.close();
          compRef = null;
        }

        if (isNullOrUndefined(item)) {
          compRef = null;
        } else {
          compRef = componentFactory(item);
          compRef.result.then(onFullfill, onError);
        }
      });

    if (!resourceHandler.isReady()) {
      resourceHandler.load();
    }

  }

  /**
   * Callback to refresjh the parent and children list after an action was successful.
   */
  private updateLists() {
    this.buildParentList(this.guiZone);
    this.buildChildrenList(this.guiZone);
    this.projectService.refreshCurrent();
    if (this.afterDelSubscription) {
      this.afterDelSubscription.unsubscribe();
      this.afterDelSubscription = null;
    }
    if (this.afterAddSubscription) {
      this.afterAddSubscription.unsubscribe();
      this.afterAddSubscription = null;
    }
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_develop@2/app/Resources/Public/src/app/modules/models/gui-zone/components/gui-zone-form/gui-zone-form.component.ts